/**
 * Generates a unique signature for a head element to detect duplicates.
 */
const createHeadElementSignature = (element: any): string | null => {
  const tag = element.tagName?.toLowerCase();
  if (!tag) return null;

  const getAttr = (name: string) => element.getAttribute?.(name);
  const getContent = () => element.textContent?.trim() || "";

  switch (tag) {
    case "meta": {
      const charset = getAttr("charset");
      const name = getAttr("name");
      const property = getAttr("property");
      if (charset) return `meta[charset]`;
      if (name) return `meta[name=${name}]`;
      if (property) return `meta[property=${property}]`;
      return null;
    }
    case "link": {
      const rel = getAttr("rel");
      const href = getAttr("href");
      return rel && href ? `link[rel=${rel}][href=${href}]` : null;
    }
    case "script": {
      const src = getAttr("src");
      if (src) return `script[src=${src}]`;
      const text = getContent();
      return text ? `script[inline:${text.slice(0, 100)}]` : null;
    }
    case "style": {
      const text = getContent();
      return text ? `style[inline:${text.slice(0, 100)}]` : null;
    }
    case "noscript": {
      const text = getContent();
      return text ? `noscript[inline:${text.slice(0, 100)}]` : null;
    }
    default:
      return null;
  }
};

/**
 * Validates if a head node has the required attributes and content.
 */
const isAllowedHeadElement = (element: any): boolean => {
  const tag = element.tagName?.toLowerCase();
  if (!tag) return false;

  const hasAttr = (...attrs: string[]) =>
    attrs.some((a) => element.hasAttribute?.(a));
  const hasContent = () => !!element.textContent?.trim();

  switch (tag) {
    case "meta":
      return hasAttr("charset", "name", "property", "http-equiv");
    case "link":
      return hasAttr("rel", "href");
    case "script":
      return !!element.getAttribute?.("src") || hasContent();
    case "style":
    case "noscript":
      return hasContent();
    case "title":
      return hasContent();
    default:
      return true;
  }
};

/**
 * Injects custom head code into the document head, with deduplication and validation.
 */
export const injectHeadCode = (
  customHeadCode: string,
  nonce?: string
): void => {
  if (!customHeadCode) return;

  const parsedFragment = document
    .createRange()
    .createContextualFragment(customHeadCode);
  const headElement = document.head;

  // Collect existing signatures
  const seenSignatures = new Set<string>();
  for (const headChild of Array.from(headElement.children)) {
    const signature = createHeadElementSignature(headChild);
    if (signature) seenSignatures.add(signature);
  }

  // Build cleaned fragment with deduplication
  const insertionFragment = document.createDocumentFragment();
  for (const sourceElement of Array.from(parsedFragment.children)) {
    if (
      !sourceElement ||
      !(sourceElement as any).tagName ||
      !isAllowedHeadElement(sourceElement)
    )
      continue;

    const signature = createHeadElementSignature(sourceElement);
    if (signature && seenSignatures.has(signature)) continue;
    if (signature) seenSignatures.add(signature);

    const clonedElement = (sourceElement as any).cloneNode(true);

    // Apply nonce to script/style/link elements (link sometimes required for strict CSP on stylesheets)
    if (["SCRIPT", "STYLE", "LINK"].includes(clonedElement.tagName)) {
      const existingNonce = clonedElement.getAttribute?.("nonce");
      if (existingNonce === "undefined")
        clonedElement.removeAttribute?.("nonce");
      if (!clonedElement.getAttribute?.("nonce") && nonce) {
        clonedElement.setAttribute?.("nonce", nonce);
      }
    }

    insertionFragment.appendChild(clonedElement);
  }

  if (!insertionFragment.childNodes.length) return;

  // Find insertion point after critical tags
  const criticalSelectors = [
    "meta[charset]",
    'meta[name="viewport"]',
    'meta[http-equiv="x-ua-compatible"]',
    "base"
  ];

  let insertAfter: any = null;
  for (const headChild of Array.from(headElement.children)) {
    if (criticalSelectors.some((sel) => (headChild as any).matches?.(sel))) {
      insertAfter = headChild;
    } else if (insertAfter) {
      break;
    }
  }

  // Insert fragment
  try {
    if (insertAfter) {
      if (typeof insertAfter.after === "function") {
        insertAfter.after(insertionFragment);
      } else {
        headElement.insertBefore(insertionFragment, insertAfter.nextSibling);
      }
    } else {
      headElement.prepend(insertionFragment);
    }
  } catch {
    headElement.appendChild(insertionFragment);
  }
};
